import { ApiClientsError } from '#pplx/frontend/api-clients/core/errors.js';
import { API_TIMEOUT_TIERS } from '#pplx/frontend/api-clients/core/utils.js';
import { normalizeUrl } from '#pplx/frontend/common/files/urls.js';
import { COMET_BROWSER_INFO_COOKIE } from '#pplx/frontend/cookies/constants.js';
import { getCookie } from '#pplx/frontend/cookies/provider.js';
import { REQUEST_ID_HEADER } from '#pplx/frontend/fetcher/constants.js';
import { parseInUserTimezone } from '#pplx/frontend/i18n/timestamps.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { startTimer } from '#pplx/frontend/metrics/browser.js';
import { browserClient } from '#pplx_generated/typespec/rest/browser/client.js';
import { memoize } from 'lodash-es';
// Keep in sync with Comet's `chrome/browser/perplexity/perplexity_profile_utils.cc`.
export var BrowserFlavor;
(function (BrowserFlavor) {
    BrowserFlavor[BrowserFlavor["CLASSIC"] = 0] = "CLASSIC";
    BrowserFlavor[BrowserFlavor["LUNA"] = 1] = "LUNA";
})(BrowserFlavor || (BrowserFlavor = {}));
/**
 * Check if Comet profile is off-the-record.
 *
 * OTR covers Incognito mode AND Guest profile in Comet browser
 */
export function isCometOffTheRecordProfile() {
    const cometInfo = getCometBrowserInfo();
    if (!cometInfo)
        return false;
    // TODO(vlad): use explicit check for off-the-record profile after https://linear.app/perplexity/issue/BRO-6730
    // for now we are using fact of missing device_id for detecting off-the-record profile
    return !cometInfo.device_id;
}
/**
 * Universal method to detect if the browser is Comet browser.
 *
 * This check may return `false` for off-the-record profile in Comet browser.
 * Use `isCometOffTheRecordProfile` for such cases!
 */
export function isCometBrowser() {
    return Boolean(getCometBrowserInfo() && !isCometOffTheRecordProfile());
}
export function isCometMobileBrowser() {
    const cometInfo = getCometBrowserInfo();
    return Boolean(cometInfo?.android_version || cometInfo?.ios_version);
}
export function getCometBrowserFlavor() {
    return getCometBrowserInfo()?.flavor ?? BrowserFlavor.CLASSIC;
}
export function isLunaCometBrowser() {
    return getCometBrowserFlavor() === BrowserFlavor.LUNA;
}
export function uncachedGetCometBrowserInfo() {
    const str = getCookie(COMET_BROWSER_INFO_COOKIE);
    if (!str) {
        return;
    }
    try {
        return JSON.parse(str);
    }
    catch {
        // Ignore
        return;
    }
}
/**
 * Get the comet browser info from the cookie.
 * @returns The comet browser info or undefined if the cookie is not set.
 */
export const getCometBrowserInfo = memoize(uncachedGetCometBrowserInfo, () => Math.floor(Date.now() / (6 * 1e4)));
const constructUserSelectedText = (userSelection, selectionTag = 'selected_by_user_text') => {
    if (!userSelection)
        return '';
    // NOTE: This XML structure is parsed on the backend in:
    // pplx/common/core/chat/prompt_utils/__init__.py::extract_selected_text_from_content()
    // If you change this format, update the regex pattern there too!
    //
    // TODO: Ideally this should be passed as a separate field instead of
    // embedding XML in content to avoid this hacky parsing approach.
    return `\n<${selectionTag}>
<selection_timestamp>${new Date(userSelection.timestamp).toLocaleString()}</selection_timestamp>
${userSelection.text}
</${selectionTag}>`;
};
const getSidecarContext = (includeSidecar, cometState, content, maxChars) => {
    if (!includeSidecar)
        return [];
    const userSelection = cometState.actions.getUserSelection();
    const userSelectedText = constructUserSelectedText(userSelection);
    const result = [];
    if (content.status === 'fulfilled') {
        const contents = content.value?.contents || (content.value?.content ? [content.value.content] : []);
        for (const { markdown = '', url = '', title = '', og_meta = {}, pdp_data, id } of contents) {
            const userSelectionText = userSelectedText;
            result.push({
                content: [markdown.slice(0, maxChars - userSelectionText.length) + userSelectionText],
                url,
                title,
                content_type: 'markdown',
                content_origin: 'current_tab',
                og_title: og_meta?.['title'],
                og_description: og_meta?.['description'],
                pdp_data: pdp_data,
                tab_id: String(id),
            });
        }
    }
    else {
        result.push({
            content: [],
            url: cometState.sidecarSourceTab.url,
            title: cometState.sidecarSourceTab.title,
            tab_id: String(cometState.sidecarSourceTab.tabId),
            content_type: 'markdown',
            content_origin: 'current_tab',
        });
        if (cometState.sidecarSourceTab.secondaryTab) {
            result.push({
                content: [],
                url: cometState.sidecarSourceTab.secondaryTab.url,
                title: cometState.sidecarSourceTab.secondaryTab.title,
                tab_id: String(cometState.sidecarSourceTab.secondaryTab.tabId),
                content_type: 'markdown',
                content_origin: 'current_tab',
            });
        }
    }
    return result;
};
const getAttachedTabsContext = (attachedTabs, attachedTabsResponse, maxChars) => {
    const res = [];
    attachedTabs.forEach((tab) => {
        const item = attachedTabsResponse.status === 'fulfilled' &&
            attachedTabsResponse.value.contents?.find((item) => item.id === tab.id);
        if (!item) {
            res.push({
                tab_id: String(tab.id),
                content: [],
                url: tab.url,
                title: '',
                content_type: 'markdown',
                content_origin: 'open_tab',
            });
            return;
        }
        res.push({
            tab_id: String(item.id),
            content: [(item.markdown ?? '').slice(0, maxChars)],
            url: item.url ?? '',
            title: item.title ?? '',
            content_type: 'markdown',
            content_origin: 'open_tab',
            og_title: item.og_meta?.['title'],
            og_description: item.og_meta?.['description'],
            pdp_data: item.pdp_data,
        });
    });
    return res;
};
const convertNavigationResultsToClientSearchResults = (navigationResults) => {
    return navigationResults.map(({ url, title, snippet, site_name, sitelinks }) => ({
        url,
        title,
        snippet,
        is_navigational: true,
        site_name: site_name ?? null,
        sitelinks: sitelinks?.map(({ title, url, snippet }) => ({
            title,
            url,
            snippet: snippet ?? null,
        })) ?? null,
    }));
};
export const sendClientPayloadAsync = async ({ clientPayloadCacheKey, cometBrowser, maxChars, includeSidecar, attachedTabs, reason, cometState, requestId, navigationResults = [], }) => {
    if (!cometBrowser)
        return;
    const clientContext = [];
    // TODO @artem remove it after Comet Mobile will stop sending current page as mention
    const uniqContextItems = new Set();
    const cometInfo = getCometBrowserInfo();
    const metadata = {
        contextType: 'runtime',
        queryContextTimeMs: 0,
        processContextTimeMs: 0,
        getNavigationItemsTimeMs: null,
        filteredItemsCount: null,
        version: cometInfo.browser_version,
        mainExtVersion: cometInfo.agent_extension_version,
    };
    const startSendContextTimer = startTimer({
        context_type: metadata?.contextType,
        browser_version: metadata?.version,
        main_ext_version: metadata?.mainExtVersion,
        content_type: metadata?.contentType,
    });
    try {
        logger.info('fetching sidecar context', { request_id: requestId });
        const [sidecarResponse, attachedTabsResponse] = await Promise.allSettled([
            includeSidecar
                ? cometBrowser.GetSidecarContext({
                    key: clientPayloadCacheKey,
                    request_id: requestId,
                })
                : Promise.resolve(undefined),
            attachedTabs.length
                ? cometBrowser.GetContent({
                    key: clientPayloadCacheKey,
                    pages: [...attachedTabs],
                    request_id: requestId,
                })
                : Promise.resolve({
                    contents: [],
                }),
        ]);
        const sidecarContext = getSidecarContext(includeSidecar, cometState, sidecarResponse, maxChars);
        sidecarContext.forEach((item) => {
            const tabId = item.tab_id ?? '-1';
            if (uniqContextItems.has(tabId))
                return;
            uniqContextItems.add(tabId);
            clientContext.push(item);
        });
        if (attachedTabs.length) {
            const attachedTabsContext = getAttachedTabsContext(attachedTabs, attachedTabsResponse, maxChars);
            attachedTabsContext.forEach((item) => {
                const tabId = item.tab_id ?? '-1';
                if (uniqContextItems.has(tabId))
                    return;
                uniqContextItems.add(tabId);
                clientContext.push(item);
            });
        }
        metadata.queryContextTimeMs =
            performance.now() - startSendContextTimer.getStartTimeFromTimeOrigin();
        logger.info('sending sidecar context', { request_id: requestId });
        const { error, response } = await browserClient.POST('/rest/browser/client_payload', reason, {
            backOffTime: 100,
            numRetries: 2,
            timeoutMs: API_TIMEOUT_TIERS.HIGH,
            body: {
                client_payload_cache_key: clientPayloadCacheKey,
                // @ts-expect-error TODO: fix this
                client_context: clientContext || [],
                ...(navigationResults.length > 0 && {
                    client_search_results: convertNavigationResultsToClientSearchResults(navigationResults),
                }),
                // @ts-expect-error TODO: fix this
                metadata,
            },
            headers: {
                [REQUEST_ID_HEADER]: requestId,
            },
        });
        startSendContextTimer.addTiming('browser.client_context.overall_time_ms');
        if (error) {
            throw new ApiClientsError('API_CLIENTS_ERROR', {
                details: {
                    clientPayloadCacheKey,
                },
                cause: error,
                status: response.status ?? 0,
            });
        }
    }
    catch (ex) {
        logger.error('Error sending client payload for cache key ', clientPayloadCacheKey, ex, {
            request_id: requestId,
        });
    }
};
export const openTabAsync = async ({ url, cometBrowser, stepUuid, extraHeaders, reason, }) => {
    const response = cometBrowser.openTab({
        url,
        key: stepUuid,
        request_id: extraHeaders[REQUEST_ID_HEADER],
    });
    await sendToolResultAsync(response, stepUuid, extraHeaders, reason);
};
export const getTopMostUrls = async ({ cometBrowser, maxUrls = 20, }) => {
    if (!cometBrowser)
        return undefined;
    try {
        const result = await cometBrowser.getTopMostVisitedUrls({ maxUrls }, 'get-top-most-urls-for-ntp');
        return result.is_success ? result.mostVisitedUrls : undefined;
    }
    catch (ex) {
        logger.error('Error fetching top most urls browser', ex);
        return undefined;
    }
};
export const sendToolFailureAsync = async (payload, extraHeaders, reason) => {
    try {
        const { error, response } = await browserClient.POST('/rest/browser/tool_failure', reason, {
            body: {
                client_payload_cache_key: payload.clientPayloadCacheKey,
                error_type: payload.errorType,
                error_msg: payload.errorMsg,
            },
            backOffTime: 100,
            numRetries: 2,
            headers: extraHeaders,
            timeoutMs: API_TIMEOUT_TIERS.MEDIUM,
        });
        if (error) {
            throw new ApiClientsError('API_CLIENTS_ERROR', {
                details: {
                    clientPayloadCacheKey: payload.clientPayloadCacheKey,
                },
                cause: error,
                status: response.status ?? 0,
            });
        }
    }
    catch (ex) {
        logger.error('Error while sending a failure of tool', ex, {
            request_id: extraHeaders[REQUEST_ID_HEADER],
        });
    }
};
export const sendToolResultAsync = async (resultTask, clientPayloadCacheKey, extraHeaders, reason) => {
    try {
        const payload = await resultTask;
        const { error, response } = await browserClient.POST('/rest/browser/tool_result', reason, {
            body: {
                client_payload_cache_key: clientPayloadCacheKey,
                payload: payload,
            },
            backOffTime: 100,
            numRetries: 2,
            headers: extraHeaders,
            timeoutMs: API_TIMEOUT_TIERS.MEDIUM,
        });
        if (error) {
            throw new ApiClientsError('API_CLIENTS_ERROR', {
                details: {
                    clientPayloadCacheKey,
                },
                cause: error,
                status: response.status ?? 0,
            });
        }
    }
    catch (e) {
        const message = (e && e.message);
        const errorMsg = message || `sendToolResult error: ${e}`;
        const error = 'errorType' in e ? e : { errorType: 'unhandled', errorMsg };
        logger.error('Error while sending tool result', error.errorType, error.errorMsg, {
            request_id: extraHeaders[REQUEST_ID_HEADER],
        });
        return sendToolFailureAsync({ clientPayloadCacheKey, ...error }, extraHeaders, reason);
    }
};
/**
 * Inside browser we uses defaults colors for API and then remap them to designed colors in C++.
 */
const CUSTOM_TO_INTERNAL_COLOR_MAP = {
    red: 'red',
    maroon: 'blue',
    orange: 'cyan',
    teal: 'green',
    grey: 'grey',
    brown: 'orange',
    green: 'pink',
    gold: 'purple',
    purple: 'red', // yes its red again, but in C++ we use purple for this color
    blue: 'yellow',
};
export const INTERNAL_TO_CUSTOM_COLOR_MAP = Object.fromEntries(Object.entries(CUSTOM_TO_INTERNAL_COLOR_MAP).map(([external, internal]) => [
    internal,
    external,
]));
export const convertBrowserColorToCustom = (color) => {
    return INTERNAL_TO_CUSTOM_COLOR_MAP[color] ?? 'grey';
};
export async function handleGroupTabs(payload, cometBrowser, stepUuid, extraHeaders, reason) {
    const response = Promise.allSettled(payload.map(async (item) => cometBrowser.groupTabs({
        tabIds: item.tab_ids ?? [],
        collapsed: item.collapsed,
        color: CUSTOM_TO_INTERNAL_COLOR_MAP[item.color] ??
            CUSTOM_TO_INTERNAL_COLOR_MAP['grey'],
        groupId: item.group_id,
        title: item.title,
    }, stepUuid, extraHeaders[REQUEST_ID_HEADER] ?? 'unknown'))).then((tabGroupResponses) => {
        return {
            groups: tabGroupResponses
                .map((item) => {
                if (item.status == 'fulfilled' && item.value.is_success) {
                    return {
                        ...item.value.tabGroup,
                        color: convertBrowserColorToCustom(item.value.tabGroup.color),
                    };
                }
                return undefined;
            })
                .filter((item) => item !== undefined),
        };
    });
    await sendToolResultAsync(response, stepUuid, extraHeaders, reason);
}
export async function handleCloseTabs(payload, cometBrowser, stepUuid, extraHeaders, reason) {
    const response = cometBrowser.closeTabs({ tabIds: payload.tab_ids }, stepUuid, extraHeaders[REQUEST_ID_HEADER] ?? 'unknown');
    await sendToolResultAsync(response, stepUuid, extraHeaders, reason);
}
export async function sendSearchBrowserAsync(payload, cometBrowser, stepUuid, extraHeaders, reason) {
    const sourceMap = {
        OPEN_TAB: 'OPEN_TABS',
        RECENTLY_CLOSED_TAB: 'RECENTLY_CLOSED_TABS',
        BROWSER_HISTORY: 'HISTORY',
    };
    const sources = (payload.browser_content_sources ?? [])
        .map((source) => sourceMap[source])
        .filter((s) => Boolean(s));
    const resultTask = cometBrowser.SearchBrowser({
        closed_tabs_max_results: payload.max_results ?? 0,
        history_max_results: payload.max_results ?? 0,
        open_tabs_max_results: payload.max_results ?? 0,
        request_id: extraHeaders[REQUEST_ID_HEADER] ?? 'unknown',
        key: stepUuid,
        queries: Array.isArray(payload.queries) ? [...payload.queries] : [],
        sources: sources,
        start_time: payload.start_time
            ? Date.now() - parseInUserTimezone(payload.start_time)
            : undefined,
        end_time: payload.end_time ? Date.now() - parseInUserTimezone(payload.end_time) : undefined,
    });
    await sendToolResultAsync(resultTask, stepUuid, extraHeaders, reason);
}
export const getSuggestsFromQuickActions = async ({ entropyBrowser, url, }) => {
    const quickActions = await entropyBrowser.getQuickActions('get-quick-actions-for-sidecar-suggests');
    if (!quickActions.is_success)
        return [];
    return quickActions.buttons.map(({ prompt }) => ({
        query: prompt,
        image: getBrowserFaviconUrl(url),
    }));
};
const PROTOCOL_REGEXP = /^\w+:\/\//;
let hasBrowserFaviconSupport = null;
export const checkBrowserFaviconSupport = () => {
    if (!isCometBrowser() || hasBrowserFaviconSupport !== null) {
        return;
    }
    const img = new Image();
    img.onerror = () => {
        hasBrowserFaviconSupport = false;
        console.warn('[Comet] chrome://favicon/ is not supported. Using our backend as fallback.');
    };
    img.onload = () => {
        hasBrowserFaviconSupport = true;
    };
    img.src = 'chrome://favicon/';
};
/**
 * Get the favicon URL from internal backend for a domain
 */
export const getBrowserFaviconUrl = (url, size = 64, scale = '3x') => {
    if (!hasBrowserFaviconSupport) {
        return `https://www.perplexity.ai/rest/browser/favicon?url=${normalizeUrl(url)}`;
    }
    // Without the protocol, chrome will consider the request invalid
    // and the default favicon in the minimum resolution (16px, 1x)
    const safeUrl = PROTOCOL_REGEXP.test(url) ? url : `https://${url}`;
    let decodedUrl;
    try {
        decodedUrl = decodeURIComponent(safeUrl);
    }
    catch {
        decodedUrl = safeUrl;
    }
    // Escape problematic characters that can cause issues in chrome://favicon URLs
    const escapedUrl = decodedUrl
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"')
        .replace(/'/g, "\\'")
        .replace(/\s/g, '\\ ');
    // We need full url here because chrome://favicon/ caches full url
    return `chrome://favicon/size/${size}@${scale}/${escapedUrl}`;
};
/**
 * Check if Comet browser version is at least the specified major.minor version.
 * Version format: major.minor.patch.build (e.g., "143.2.7499.33687")
 */
export const isCometVersionAtLeast = (minMajor, minMinor = 0) => {
    try {
        const browserInfo = getCometBrowserInfo();
        if (!browserInfo)
            return false;
        const versionParts = browserInfo.browser_version.split('.');
        const major = parseInt(versionParts[0] ?? '', 10);
        const minor = parseInt(versionParts[1] ?? '', 10);
        if (isNaN(major))
            return false;
        if (major > minMajor)
            return true;
        if (major < minMajor)
            return false;
        return !isNaN(minor) && minor >= minMinor;
    }
    catch {
        return false;
    }
};
export const findInstalledManifestForServer = (installedDxts, server) => {
    return server.dxt_name
        ? installedDxts?.find((dxt) => dxt.name === server.dxt_name)?.manifest
        : undefined;
};
export const getSidecarThreadId = (sidecarSourceTabId, sidecarSourceSecondaryTabId) => {
    return (sidecarSourceSecondaryTabId
        ? [sidecarSourceTabId, sidecarSourceSecondaryTabId].sort().join('-')
        : sidecarSourceTabId.toString());
};
/**
 * Returns all possible key combinations for split-view tabs
 */
export const getSidecarThreadIdsForSplitView = (sidecarSourceTabId, sidecarSourceSecondaryTabId) => {
    if (!sidecarSourceSecondaryTabId)
        return [sidecarSourceTabId.toString()];
    const splitViewKey = getSidecarThreadId(sidecarSourceTabId, sidecarSourceSecondaryTabId);
    return [
        splitViewKey,
        sidecarSourceTabId.toString(),
        sidecarSourceSecondaryTabId.toString(),
    ];
};
const WELL_KNOWN_PROMPT_SOURCES = [
    'homepage_widget',
    'comet_magic_link', // try assistant widget
    'comet_quick_action',
    'comet_selection',
    'try-assistant',
    'comet_assistant_popup',
    'try-assistant-onboarding',
    'spotlight',
    'inline-assistant',
    'comet_nudge',
];
export const isWellKnownPromptSource = (source) => {
    if (!source)
        return false;
    return WELL_KNOWN_PROMPT_SOURCES.includes(source);
};
/**
 * Parses the `attachments` URL param produced by the native shells.
 *
 * Two producer formats exist: the browser C++ (`AppendExtraParams`) writes a
 * JSON array (`["url1","url2"]`), while the legacy extension flow writes a
 * bare bracket list (`[url1,url2]`). Stripping brackets and splitting by comma
 * — the historical parsing — keeps the JSON quotes inside each URL and the
 * backend then receives corrupted attachment URLs, so try JSON first.
 */
export function parseAttachmentsParam(raw) {
    if (!raw)
        return undefined;
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            const urls = parsed.filter((url) => typeof url === 'string' && url.length > 0);
            return urls.length ? urls : undefined;
        }
    }
    catch {
        // Not JSON — fall through to the legacy bracket-list format.
    }
    const urls = raw.replace(/[[\]]/g, '').split(',').filter(Boolean);
    return urls.length ? urls : undefined;
}
export const DEFAULT_API_CLASSIFIER_RESULT = {
    personal_search: false,
    skip_search: false,
    widget_type: 'GENERAL',
    hide_nav: false,
    image_generation: false,
    time_widget: false,
    hide_sources: false,
    mhe_predictions: {
        skip_search: false,
        image_generation_intent: false,
        time_widget: false,
        places_search_intent: false,
        shopping_intent: false,
        image_preview: false,
        video_preview: false,
        study_intent: false,
        personal_search: false,
        weather_widget: false,
        finance_widget_gating: false,
        finance_agent_gating: false,
        calculator_widget: false,
        comet_nav_widget_combined_target: false,
    },
    mhe_predictions_full: {
        skip_search: {
            is_true: false,
            probability: 0,
            threshold: 0,
        },
        image_generation_intent: {
            is_true: false,
            probability: 0,
            threshold: 0,
        },
        time_widget: {
            is_true: false,
            probability: 0,
            threshold: 0,
        },
        places_search_intent: {
            is_true: false,
            probability: 0,
            threshold: 0,
        },
        shopping_intent: {
            is_true: false,
            probability: 0,
            threshold: 0,
        },
        image_preview: {
            is_true: false,
            probability: 0,
            threshold: 0,
        },
        video_preview: {
            is_true: false,
            probability: 0,
            threshold: 0,
        },
        study_intent: {
            is_true: false,
            probability: 0,
            threshold: 0,
        },
        personal_search: {
            is_true: false,
            probability: 0,
            threshold: 0,
        },
        skip_personal_search: {
            is_true: false,
            probability: 0,
            threshold: 0,
        },
        weather_widget: {
            is_true: false,
            probability: 0,
            threshold: 0,
        },
        finance_widget_gating: {
            is_true: false,
            probability: 0,
            threshold: 0,
        },
        finance_agent_gating: {
            is_true: false,
            probability: 0,
            threshold: 0,
        },
        calculator_widget: {
            is_true: false,
            probability: 0,
            threshold: 0,
        },
        comet_nav_widget_combined_target: {
            is_true: false,
            probability: 0,
            threshold: 0,
        },
        domain_subdomain: undefined,
    },
};
export const DEFAULT_CLASSIFIER_API_TIMEOUT_MS = 1500;
export const fetchClassifierResults = async (query, timeoutMs = DEFAULT_CLASSIFIER_API_TIMEOUT_MS) => {
    const { data } = await browserClient.POST('/rest/browser/classify', 'mobile_search_injection', {
        body: {
            query,
        },
        timeoutMs,
    });
    return data?.results;
};
/**
 * Check if it's ready to ask permission
 *
 * We don't want to show ask permission if:
 * - Permission check hasn't completed yet
 * - Permission is already granted
 */
export const isReadyToAskPermission = (query) => !query.isFetching && query.data === false;
/**
 * Determines if a mouse event should open links in a new tab/window.
 * Special keys include Ctrl/Cmd/Shift/Alt or middle mouse button.
 */
/**
 * Extracts the pathname starting from `/search`, discarding all prefixes.
 *
 * @param pathname - The URL pathname to process
 * @returns The portion of pathname starting from `/search` (e.g., `/search/abc123`),
 *          or the original pathname if `/search` is not found
 *
 * @example
 * extractSearchPath('/search/abc123'); => '/search/abc123'
 * extractSearchPath('/inline-assistant/search/abc123'); => '/search/abc123'
 * extractSearchPath('/other/path'); => '/other/path'
 */
export const extractSearchPath = (pathname) => {
    const searchIndex = pathname.indexOf('/search');
    return searchIndex !== -1 ? pathname.substring(searchIndex) : pathname;
};
export const ENTROPY_SOURCE = 'entropy';
export const COMET_MOBILE_IOS = 'browser_ios';
export const COMET_MOBILE_ANDROID = 'browser_android';
export const getCometSource = () => {
    const cometInfo = getCometBrowserInfo();
    if (cometInfo?.ios_version) {
        return COMET_MOBILE_IOS;
    }
    if (cometInfo?.android_version) {
        return COMET_MOBILE_ANDROID;
    }
    return ENTROPY_SOURCE;
};
export const getInlineAssistantDraggableRegionId = (region) => {
    const x = Math.floor(region.origin.x);
    const y = Math.floor(region.origin.y);
    const width = Math.floor(region.size.width);
    const height = Math.floor(region.size.height);
    return `${x}-${y}-${width}-${height}`;
};
// Synchronizes inline assistant parameters from URL to comet state.
export function syncInlineAssistantParamsFromURL({ cometAdapter, urlSearchParams, }) {
    const urlParams = urlSearchParams ?? new URLSearchParams(window.location.search);
    const action = urlParams.get('action');
    const isEditable = urlParams.get('is_editable');
    if (!action) {
        return false;
    }
    cometAdapter
        .getStore()
        .getState()
        .actions.setInlineAssistantParams({
        action,
        isWritingMode: isEditable === 'true',
    });
    return true;
}
//# sourceMappingURL=utils.js.map